/**
 * Multi-assignee model shared by WorkOrder and ProjectModel.
 *
 * A task carries any combination of internal assignees and external delegates
 * at once, each stored in a UID-keyed map so add/remove is a single-path atomic
 * update that never disturbs the others (see the multiple-assignees plan).
 *
 * Legacy single-assignee records (scalar `assignedTo` / `externalUserId` +
 * `guestTokenKey`) stay readable via the normalizers below: they fold a legacy
 * scalar into a single-entry map on read. Writers always emit the map shape and
 * delete the legacy scalars. The normalizers tolerate RTDB quirks (object-shaped
 * values, corrupt entries) the same way getComponentKeys does.
 */

/** Metadata stored per internal assignee, keyed by the assignee's UID. */
export interface AssigneeMeta {
    assignedBy: string; // UID of the admin who assigned
    assignedAt: string; // YYYY-MM-DD hh:mm:ss (Stockholm)
}

/**
 * Metadata stored per external delegate, keyed by the delegate's externalUserId.
 * The delegation itself is the access grant: the delegate opens the task with
 * their personal guest link (guestLinks, read through the callables).
 */
export interface DelegationMeta {
    /** @deprecated per-task token of the pre-2026-10 model; no longer written, ignored by readers */
    guestTokenKey?: string;
    delegatedBy: string; // UID of the admin who delegated
    delegatedAt: string; // YYYY-MM-DD hh:mm:ss (Stockholm)
    /** Projects only: the guest's "jag är klar" signal */
    completedAt?: string;
    completedNote?: string;
}

/**
 * Loose shape accepted by the normalizers — anything carrying the new maps
 * and/or the legacy scalars. WorkOrder and ProjectModel both satisfy it.
 */
export interface AssignableRecord {
    assignees?: { [uid: string]: AssigneeMeta };
    delegations?: { [externalUserId: string]: DelegationMeta };
    // Legacy scalars (read-only via the shim; never written going forward)
    assignedTo?: string;
    externalUserId?: string;
    guestTokenKey?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Normalized map of internal assignees. Folds a legacy scalar `assignedTo` into
 * a single-entry map; when a real `assignees` map is present it wins (legacy
 * scalar ignored, no duplicate). Never throws — invalid entries are skipped.
 */
export function getAssignees(resource: AssignableRecord | null | undefined): Record<string, AssigneeMeta> {
    if (!resource) {
        return {};
    }

    const result: Record<string, AssigneeMeta> = {};
    const raw = resource.assignees;
    if (isRecord(raw)) {
        for (const [uid, meta] of Object.entries(raw)) {
            if (!uid) {
                continue;
            }
            const m: Record<string, unknown> = isRecord(meta) ? meta : {};
            result[uid] = {
                assignedBy: typeof m.assignedBy === 'string' ? m.assignedBy : '',
                assignedAt: typeof m.assignedAt === 'string' ? m.assignedAt : ''
            };
        }
    }

    // Legacy scalar folds in only if the map form isn't already present for it.
    const legacy = resource.assignedTo;
    if (typeof legacy === 'string' && legacy.length > 0 && !result[legacy]) {
        result[legacy] = { assignedBy: '', assignedAt: '' };
    }

    return result;
}

/**
 * Normalized map of external delegations. Folds a legacy `externalUserId` +
 * `guestTokenKey` pair into a single-entry map; a real `delegations` map wins.
 * Never throws — corrupt entries are skipped.
 */
export function getDelegations(resource: AssignableRecord | null | undefined): Record<string, DelegationMeta> {
    if (!resource) {
        return {};
    }

    const result: Record<string, DelegationMeta> = {};
    const raw = resource.delegations;
    if (isRecord(raw)) {
        for (const [externalUserId, meta] of Object.entries(raw)) {
            if (!externalUserId || !isRecord(meta)) {
                continue;
            }
            result[externalUserId] = {
                delegatedBy: typeof meta.delegatedBy === 'string' ? meta.delegatedBy : '',
                delegatedAt: typeof meta.delegatedAt === 'string' ? meta.delegatedAt : '',
                ...(typeof meta.completedAt === 'string' ? { completedAt: meta.completedAt } : {}),
                ...(typeof meta.completedNote === 'string' ? { completedNote: meta.completedNote } : {}),
            };
        }
    }

    const legacyUser = resource.externalUserId;
    const legacyToken = resource.guestTokenKey;
    if (
        typeof legacyUser === 'string' &&
        legacyUser.length > 0 &&
        typeof legacyToken === 'string' &&
        legacyToken.length > 0 &&
        !result[legacyUser]
    ) {
        result[legacyUser] = { delegatedBy: '', delegatedAt: '' };
    }

    return result;
}

/** True when a task has no internal assignees and no external delegates. */
export function isUnassigned(resource: AssignableRecord | null | undefined): boolean {
    return (
        Object.keys(getAssignees(resource)).length === 0 &&
        Object.keys(getDelegations(resource)).length === 0
    );
}
